import type { AvailableClassData } from './server/server-actions';

export interface AgendaDay {
  date: string;
  classes: AvailableClassData[];
}

export function toDateKey(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseDateKey(key: string) {
  return new Date(`${key}T00:00:00`);
}

/** Days with classes, in date then start-time order. */
export function groupClassesByDay(classes: AvailableClassData[]): AgendaDay[] {
  const sorted = [...classes].sort(
    (a, b) =>
      a.booking_date.localeCompare(b.booking_date) ||
      a.start_time.localeCompare(b.start_time),
  );
  const days = new Map<string, AvailableClassData[]>();

  for (const classData of sorted) {
    const day = days.get(classData.booking_date);
    if (day) day.push(classData);
    else days.set(classData.booking_date, [classData]);
  }

  return [...days].map(([date, dayClasses]) => ({ date, classes: dayClasses }));
}

/**
 * Every calendar day from today (or the first class day, if earlier) to the
 * last class day, so empty days show greyed out between the class days.
 */
export function agendaStripDates(days: AgendaDay[], today = new Date()) {
  const first = days[0]?.date;
  const last = days.at(-1)?.date;
  if (!first || !last) return [];

  const todayKey = toDateKey(today);
  const cursor = parseDateKey(first < todayKey ? first : todayKey);
  const end = parseDateKey(last);
  const dates: Date[] = [];

  while (cursor <= end) {
    dates.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return dates;
}

/**
 * Where the reader is: the last section whose top has reached `line` (the
 * bottom of the sticky strip), or the first before any has, plus how far
 * through it they are (0 at its top, 1 where the next section begins). The
 * strip uses the progress to glide between day pills instead of stepping.
 */
export function agendaScrollPosition(
  sections: Array<{ date: string; top: number }>,
  line: number,
) {
  let index = 0;
  for (let i = 1; i < sections.length; i++) {
    if ((sections[i]?.top ?? 0) > line) break;
    index = i;
  }

  const current = sections[index];
  const next = sections[index + 1];
  const span = current && next ? next.top - current.top : 0;
  const progress =
    current && span > 0
      ? Math.min(1, Math.max(0, (line - current.top) / span))
      : 0;

  return { date: current?.date, index, progress };
}
